import { useState } from "react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { HelpCircle, ShieldCheck, Globe2, Sparkles, Lock } from "lucide-react";

/**
 * One-click "Why do we need this information?" helper used on KYC,
 * Accreditation, and Identity sections. Surfaces the EchoSense
 * differentiation in a compliance-safe wrapper so members understand
 * the *value* behind the data collection — not just the regulatory ask.
 */
const WhyWeAskDialog = ({
  triggerLabel = "Why do we need this information?",
  variant = "ghost",
  className = "",
  testId = "why-we-ask-btn",
}) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className={`gap-2 text-azure hover:text-azure-light hover:bg-azure/10 px-3 py-1 h-auto font-medium ${className}`}
      >
        <HelpCircle size={14} />
        {triggerLabel}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="why-we-ask-dialog"
          className="bg-surface border border-azure/30 text-platinum max-w-xl"
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400/30 to-amber-600/10 border border-amber-400/40 flex items-center justify-center">
                <Sparkles className="text-gold" size={18} />
              </div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold-dark font-semibold">
                Why we ask
              </p>
            </div>
            <DialogTitle className="text-2xl font-serif text-cream leading-snug">
              Members-only access to opportunities the public never sees.
            </DialogTitle>
            <DialogDescription className="text-platinum/85 leading-relaxed pt-3 text-[15px]">
              EchoSense Capital Club is a members-only platform that introduces qualified
              clients to a curated network of global trading partners — partners delivering
              return profiles rarely seen by the general public.
              <br /><br />
              Large institutions are content to give the everyday consumer a very low
              monthly and annual return while keeping the larger profit possibilities
              quiet. <span className="text-gold-dark font-semibold">We change that.</span>
              <br /><br />
              Please allow us to serve you and expose you to these opportunities — the
              identity, accreditation, and source-of-funds information you provide is what
              lets our compliance team route your file to the right partner and unlock the
              right tier of the Deal Room.
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/[0.04] p-3">
              <ShieldCheck className="text-azure mb-1.5" size={18} />
              <p className="text-xs uppercase tracking-wider text-platinum-light font-semibold mb-1">
                Compliance-first
              </p>
              <p className="text-xs text-platinum/85 leading-relaxed">
                KYC, AML, and source-of-funds keep your file institutional-grade.
              </p>
            </div>
            <div className="rounded-lg border border-amber-400/20 bg-amber-400/[0.05] p-3">
              <Globe2 className="text-gold mb-1.5" size={18} />
              <p className="text-xs uppercase tracking-wider text-platinum-light font-semibold mb-1">
                Global Partners
              </p>
              <p className="text-xs text-platinum/85 leading-relaxed">
                Routed only to vetted trading partners with verified track records.
              </p>
            </div>
            <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
              <Lock className="text-emerald mb-1.5" size={18} />
              <p className="text-xs uppercase tracking-wider text-platinum-light font-semibold mb-1">
                Private & Secure
              </p>
              <p className="text-xs text-platinum/85 leading-relaxed">
                Your data stays inside the EchoSense compliance perimeter — never sold.
              </p>
            </div>
          </div>

          <p className="text-[11px] text-platinum-dark leading-relaxed mt-4">
            EchoSense Capital Club acts as an introducer only. We do not provide investment
            advice, hold client funds, or guarantee any result. All onboarding, funding,
            and account approvals are handled by the regulated partner directly.
          </p>

          <div className="flex justify-end pt-2">
            <Button
              data-testid="why-we-ask-close"
              onClick={() => setOpen(false)}
              className="bg-gold hover:bg-gold-light text-navy-dark font-semibold"
            >
              Got it — continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WhyWeAskDialog;
